import { useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  FormControlLabel,
  FormHelperText,
} from '@mui/material';
import { isValid } from 'date-fns';

// Constants
const UTILITIES_MAP = {
  electricity: 'Electricity',
  heat: 'Heat',
  water_waste: 'Water/Waste',
};

const LATE_FEE = 150.00;
const NSF_FEE = 150.00;
const PET_NON_REFUNDABLE_FEE = 350.00;

// Utility functions
const formatList = (items) => {
  if (!items?.length) return 'None';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

const formatDate = (dateString) => {
  if (!dateString || !isValid(new Date(dateString))) return '-';

  try {
    const date = new Date(dateString);
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    const getSuffix = (day) => {
      if ([1, 21, 31].includes(day)) return 'st';
      if ([2, 22].includes(day)) return 'nd';
      if ([3, 23].includes(day)) return 'rd';
      return 'th';
    };

    return `${month} ${day}${getSuffix(day)}, ${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
};

const formatAmount = (value) => {
  if (!value) return '';
  const numValue = parseFloat(value.toString().replace(/[$,]/g, '')) || 0;
  return `$${numValue.toFixed(2)}`;
};

const generatePetContent = (propertyDetails) => {
  const { allowed_pets } = propertyDetails || {};
  const { dogs = false, cats = false } = allowed_pets || {};

  if (cats && !dogs) {
    return (
      <>
        Only cats under 20 lbs. (full grown) are allowed with a {formatAmount(PET_NON_REFUNDABLE_FEE)} pet non-refundable fee.
        All animals must be spayed or neutered.
        No dogs allowed during the tenancy period.
        Violation of this policy can lead to eviction.
        Tenants must pick up after their pets.
        Management may conduct an interview with the pet if needed.
        All pets must be approved by management prior to signing the lease or during tenancy.
      </>
    );
  }

  if (cats && dogs) {
    return (
      <>
        Cats and small dogs under 20 lbs. (full grown) are allowed with a {formatAmount(PET_NON_REFUNDABLE_FEE)} pet non-refundable fee.
        All animals must be spayed or neutered.
        Dogs must always be leashed/controlled outside of the apartment while on property.
        Tenants must pick up after their pets.
        Management may conduct an interview with the pet if needed.
        All pets must be approved by management prior to signing the lease or during tenancy.
      </>
    );
  }

  if (dogs && !cats) {
    return (
      <>
        Only small dogs under 20 lbs. (full grown) are allowed with a {formatAmount(PET_NON_REFUNDABLE_FEE)} pet non-refundable fee.
        All animals must be spayed or neutered.
        No cats allowed during the tenancy period.
        Violation of this policy can lead to eviction.
        Dogs must always be leashed/controlled outside of the apartment while on property.
        Tenants must pick up after their pets.
        Management may conduct an interview with the pet if needed.
        All pets must be approved by management prior to signing the lease or during tenancy.
      </>
    );
  }

  return "No pets are allowed during the tenancy period. Violation of this policy can lead to eviction.";
};

const SectionTitle = ({ children }) => (
  <Typography variant="subtitle2" className="section-title">
    {children}
  </Typography>
);

const SectionContent = ({ children, highlighted = false, sx = {} }) => (
  <Typography
    variant="body2"
    className="section-content"
    sx={highlighted ? { backgroundColor: '#ffff99', color: 'black', p: 1, borderRadius: 1, ...sx } : sx}
  >
    {children}
  </Typography>
);

const HighlightedText = ({ children }) => (
  <span style={{ color: 'red', fontWeight: 'bold' }}>{children}</span>
);

// Security Deposits component
const SecurityDeposits = ({ values }) => {
  const securityDeposits = [
    {
      description: "Security Deposit (Refundable)",
      amount: formatAmount(values?.security_deposits?.refundable) || ""
    },
    {
      description: "Pet Security Fee (Non-Refundable)",
      amount: formatAmount(values?.security_deposits?.non_refundable) || ""
    }
  ];

  return (
    <Paper variant="outlined" className="security-details-table">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Description</TableCell>
            <TableCell>Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {securityDeposits
            .filter((deposit) => deposit.description === "Security Deposit (Refundable)")
            .map((deposit, index) => (
              <TableRow key={index}>
                <TableCell className="label">{deposit.description}</TableCell>
                <TableCell>{deposit.amount || '-'}</TableCell>
              </TableRow>
            ))}
          {securityDeposits
            .filter((deposit) => deposit.description === "Pet Security Fee (Non-Refundable)")
            .map((deposit, index) => (
              <TableRow key={index}>
                <TableCell className="label">{deposit.description}</TableCell>
                <TableCell>{deposit.amount || '-'}</TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

const Step2 = ({
  formik,
  rentPaymentConsent,
  setRentPaymentConsent,
  utilitiesConsent,
  setUtilitiesConsent,
}) => {
  const { values } = formik;

  const notIncludedUtilities = Object.entries(values?.property_details?.utilities_available || {})
    .filter(([_, isSelected]) => !isSelected)
    .map(([key]) => UTILITIES_MAP[key]);

  const utilitiesString = formatList(notIncludedUtilities);

  useEffect(() => {
    const noUtilityResponsibility =
      Object.values(formik.values?.property_details?.utilities_available || {})
        .every(isIncluded => isIncluded === true);

    if (noUtilityResponsibility && !utilitiesConsent) {
      setUtilitiesConsent(true);
    }
  }, [formik.values?.property_details?.utilities_available, utilitiesConsent]);


  return (
    <Box className="lease-form-step2">
      {/* Section 4: Security Deposit */}
      <Box className="section">
        <SectionTitle>4. SECURITY DEPOSIT/PET FEE DEPOSIT</SectionTitle>
        <SectionContent>
          The Landlord acknowledges receipt on{' '}
          <span style={{ fontWeight: 600, color: '#1976d2' }}>
            {formatDate(values?.security_deposits?.received_date)}
          </span>{' '}
          from the Tenant as a security deposit. The Security Deposit may be utilized by the Landlord in such amount
          as is necessary to provide for any or all of the following:
          <br />
          <SecurityDeposits values={values} />

          <Typography variant="subtitle2" className="section-bottom-content">
            A. The landlord is entitled to use and retain the security deposit in the event the tenant:
            does not take possession on the commencement of the term of this lease, does not pay the
            first installment of rent, or does not execute a lease agreement satisfactory to both parties.
          </Typography>

          <Typography variant="subtitle2" className="section-bottom-content">
            B. The landlord may apply such amounts as he/she deems necessary to provide for:
            <Box component="ol" sx={{ pl: 4 }}>
              <li>Repairing or replacing any fixtures, fittings, appliances, or facilities located in, upon or about the leased premises.</li>
              <li>Cleaning and arranging for cleaning of the leased premises; including appliances, windows, floors, walls, professionally steam cleaning the carpets, repairing paint work etc.</li>
              <li>To remedy a breach of an obligation of the tenant hereunder at any time.</li>
              <li style={{ backgroundColor: '#ffff99', color: 'black', borderRadius: '1px' }}>
                To cover the cost of any late rent or NSF/Returned cheque fee.
              </li>
              <li>The security deposit can NOT be used by the tenant towards any rent.</li>
            </Box>
          </Typography>
        </SectionContent>
      </Box>

      {/* Section 5: Pets */}
      <Box className="section">
        <SectionTitle>5. PETS</SectionTitle>
        <SectionContent>
          {generatePetContent(values?.property_details)}
        </SectionContent>
      </Box>

      <Box className="section">
        <SectionTitle>6. RENT PAYMENT</SectionTitle>
        <SectionContent highlighted>
          <div className="pb-2">
            <span className="text-black fw-bold">Due Date:</span> Rent is due on the{' '}
            <HighlightedText>1st day</HighlightedText> of each month.
          </div>

          <div className="pb-2">
            <span className="text-black fw-bold">Late Payment Fee:</span> A fee of{' '}
            <HighlightedText>${LATE_FEE.toFixed(2)}</HighlightedText> will be charged if the full rent is
            received on or after the <HighlightedText>2nd day</HighlightedText> of the month.
          </div>

          <div className="pb-2">
            <span className="text-black fw-bold">Non-Sufficient Funds (NSF):</span> Should any payment be returned due to non-sufficient funds, the tenant agrees to pay:
            <div className="ms-4">
              <>🔸 <HighlightedText>${NSF_FEE.toFixed(2)}</HighlightedText> NSF fee for each occurrence – due within <HighlightedText>48 hours</HighlightedText>.</><br />
              <>🔸 <HighlightedText>${LATE_FEE.toFixed(2)}</HighlightedText> late payment fee in addition – due within <HighlightedText>48 hours</HighlightedText>.</>
            </div>
            Any funds received will first be applied to the outstanding balance. All incentives may be forfeited.
          </div>

          <div className="pb-2">
            <span className="text-black fw-bold">Eviction Notice:</span> If rent is not paid by the{' '}
            <HighlightedText>1st day</HighlightedText> of the month:
            <div className="ms-4">
              <>🔸 The tenant will receive an <HighlightedText>eviction notice</HighlightedText> to vacate the property.</><br />
              <>🔸 The matter will be filed with the <HighlightedText>RTRDS</HighlightedText> unless payment arrangements have been made with management within <HighlightedText>24 hours</HighlightedText> and approved.</>
            </div>
          </div>

          <div className="pb-2">
            <span className="text-black fw-bold">Grounds for Eviction:</span> Any NSF occurrence within the first{' '}
            <HighlightedText>4 months</HighlightedText> of the lease term may constitute grounds for eviction and all incentives may be forfeited.
          </div>
        </SectionContent>

        <Box sx={{ mt: 2, pl: 2, pr: 2, pb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={rentPaymentConsent}
                onChange={(e) => setRentPaymentConsent(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" sx={{ color: '#444' }}>
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  I acknowledge and agree
                </Box>{' '}
                to all rent payment terms, including due dates, late fees, NSF charges,
                eviction procedures, and all related consequences described in this section.
              </Typography>
            }
            sx={{ alignItems: 'flex-start', mb: 1 }}
          />
          {!rentPaymentConsent && (
            <FormHelperText error sx={{ ml: '32px', mt: -0.5 }}>
              Required – please confirm your agreement
            </FormHelperText>
          )}
        </Box>
      </Box>

      <Box className="section">
        <SectionTitle>7. UTILITIES AND SERVICE</SectionTitle>
        <SectionContent highlighted>
          <div className="pb-2">
            The tenant agrees to pay, as and when due, all charges for the following utilities and services for{' '}
            <HighlightedText>{utilitiesString}</HighlightedText>.
          </div>

          {notIncludedUtilities.length > 0 && (
            <>
              <div className="pb-2">
                <span className="text-black fw-bold">Utility Account Responsibility:</span> If the tenant fails to establish any required utility account in their name, or if the landlord is required to pay a utility on the tenant’s behalf:
                <br />
                The tenant shall reimburse the actual utility costs plus an administrative fee of <HighlightedText>$150.00</HighlightedText> per utility account not properly set up.
              </div>

              <div>
                <span className="text-black fw-bold">Consequences of Non-Compliance:</span> If this amount is not collected during the tenancy, it may constitute grounds for eviction. Any outstanding amounts will be assessed during the <HighlightedText>Statement of Account (SOA)</HighlightedText> process, at which time an administrative fee of <HighlightedText>$300.00</HighlightedText> per utility account not set up will apply.
              </div>
            </>
          )}

          {notIncludedUtilities.length === 0 && (
            <div className="pb-2">
              All utilities included in rent – no separate accounts required.
            </div>
          )}
        </SectionContent>

        {notIncludedUtilities.length > 0 && (
          <Box sx={{ mt: 2, pl: 2, pr: 2, pb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={utilitiesConsent}
                  onChange={(e) => setUtilitiesConsent(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ color: '#444' }}>
                  <Box component="span" sx={{ fontWeight: 'bold' }}>
                    I acknowledge and agree
                  </Box>{' '}that I am fully responsible for setting up and paying all required
                  utilities in my name, and I understand the administrative fees, reimbursement obligations,
                  and potential eviction consequences for non-compliance as described above.
                </Typography>
              }
              sx={{ alignItems: 'flex-start', mb: 1 }}
            />
            {!utilitiesConsent && (
              <FormHelperText error sx={{ ml: '32px', mt: -0.5 }}>
                Required – please confirm your agreement
              </FormHelperText>
            )}
          </Box>
        )}
      </Box> 
    </Box>
  );
};

export { Step2 };